import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { checkLoyaltyPopup, resetLoyaltyCounter } from '../../utils/supabaseAPI';
import { useRealtimeNotifications } from '../../hooks/useRealtimeNotifications';
import AdminLoyaltyAlertModal from './AdminLoyaltyAlertModal';
import NotificationBell from '../UI/NotificationBell';
import {
  CreditCard,
  AlertCircle,
  CheckCircle,
  Clock,
  PiggyBank,
  Wallet,
  Activity,
  RefreshCw,
  ChevronRight,
  BarChart3,
  Receipt,
  Users,
  ArrowDownCircle,
  LogOut,
  AlertTriangle
} from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';
import { supabase } from '../../utils/supabaseClient';

const NAVY = '#0f2d6b';
const GOLD = '#e8a020';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isConnected } = useRealtimeNotifications();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    pendingRequests: 0,
    activeLoans: 0,
    totalInterestCollected: 0,
    pendingWithdrawals: 0,
    activeSavingsPlans: 0,
    totalUsers: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [showLoyaltyModal, setShowLoyaltyModal] = useState(false);
  const [loyaltyModalData, setLoyaltyModalData] = useState(null);

  useEffect(() => {
    loadDashboardData();
    checkAdminLoyaltyPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAdminLoyaltyPopup = async () => {
    if (user?.id) {
      try {
        const result = await checkLoyaltyPopup(user.id);
        if (result.success && result.showPopup) {
          setLoyaltyModalData(result.notification);
          setShowLoyaltyModal(true);
        }
      } catch (error) {
        console.error('[ADMIN_DASHBOARD] Erreur vérification popup fidélité:', error);
      }
    }
  };

  const handleContactUser = async (userId) => {
    // Réinitialiser le compteur et mettre à jour le statut
    try {
      const result = await resetLoyaltyCounter(userId);
      if (result.success) {
        console.log('[ADMIN_DASHBOARD] Compteur réinitialisé, nouveau statut:', result.newStatus);
        // Rediriger vers le profil utilisateur avec les stats des 5 derniers prêts
        navigate(`/admin/user-management?userId=${userId}&showStats=true`);
      }
    } catch (error) {
      console.error('[ADMIN_DASHBOARD] Erreur réinitialisation compteur:', error);
      // Rediriger quand même vers le profil
      navigate(`/admin/user-management?userId=${userId}&showStats=true`);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[ADMIN_DASHBOARD] 📥 Chargement des données...');

      const [loansRes, savingsRes, withdrawalsRes, usersRes] = await Promise.all([
        supabase.from('loans').select('id, status, amount, approved_at, interest_rate, created_at, user_id'),
        supabase.from('savings_plans').select('id, status, current_balance'),
        supabase.from('withdrawal_requests').select('id, status, amount, user_id, savings_plan_id, created_at'),
        supabase.from('users').select('id', { count: 'exact', head: true })
      ]);

      console.log('[ADMIN_DASHBOARD] ✅ Données chargées');

      // Loans
      const loans = loansRes.data || [];
      const pendingRequests = loans.filter(l => l.status === 'pending').length;
      const activeLoans = loans.filter(
        l => l.approved_at && l.status !== 'completed' && l.status !== 'rejected' && l.status !== 'pending'
      ).length;
      const totalInterestCollected = loans
        .filter(l => l.status === 'completed' && l.approved_at)
        .reduce((sum, l) => sum + Math.round((l.amount || 0) * ((l.interest_rate || 0) / 100)), 0);

      // Savings
      const savings = savingsRes.data || [];
      const activeSavingsPlans = savings.filter(p => p.status === 'active').length;

      // Withdrawals
      const withdrawals = withdrawalsRes.data || [];
      const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending').length;

      // Users
      const totalUsers = usersRes.count ?? 0;

      // Recent Activities - withdrawals first (priority), then loans
      const recentWithdrawals = withdrawals
        .filter(w => w.status === 'pending')
        .slice(0, 3)
        .map(w => ({
          id: w.id,
          type: 'withdrawal',
          status: w.status,
          amount: w.amount || 0,
          date: w.created_at,
          userId: w.user_id
        }));

      const recentLoans = loans.slice(0, 5).map(loan => ({
        id: loan.id,
        type: 'loan',
        status: loan.status,
        amount: loan.amount || 0,
        date: loan.created_at,
        userId: loan.user_id
      }));

      const allActivities = [...recentWithdrawals, ...recentLoans]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 8);

      setStats({ pendingRequests, activeLoans, totalInterestCollected, pendingWithdrawals, activeSavingsPlans, totalUsers });
      setRecentActivities(allActivities);
    } catch (error) {
      console.error('[ADMIN_DASHBOARD] ❌ Erreur:', error);
      setError(error.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };


  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 max-w-sm w-full text-center">
          <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={24} className="text-red-500" />
          </div>
          <h2 className="text-base font-bold text-slate-900 mb-2">Erreur de chargement</h2>
          <p className="text-slate-400 text-sm mb-5">{error}</p>
          <button
            onClick={loadDashboardData}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 text-white rounded-xl font-semibold"
            style={{ background: NAVY }}
          >
            <RefreshCw size={15} /> Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* ─── HERO HEADER ─────────────────────────────────────────────────────── */}
      <div
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1a3f8f 100%)` }}
      >
        {/* Decorative blobs */}
        <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-white/5 pointer-events-none" />
        <div
          className="absolute -bottom-6 -left-6 w-28 h-28 rounded-full pointer-events-none"
          style={{ background: `${GOLD}15` }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            {/* Left: Logo + Title + Greeting */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <img
                  src="/logo-campus-finance.png"
                  alt=""
                  className="w-6 h-6 object-contain"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </div>
              <div className="min-w-0">
                <p className="text-white/55 text-[11px] font-medium tracking-wide leading-none mb-0.5">
                  AB Campus Finance
                </p>
                <h1 className="text-white font-bold text-base sm:text-lg leading-tight truncate">
                  Administration&nbsp;·&nbsp;
                  <span style={{ color: GOLD }}>
                    {user?.first_name || 'Admin'}
                  </span>
                </h1>
                {/* Realtime indicator */}
                {isConnected && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-[10px] text-white/50">En ligne</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Notification + Refresh + Avatar + Logout */}
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              <NotificationBell className="sm:block" />

              <button
                onClick={loadDashboardData}
                disabled={loading}
                className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                title="Actualiser"
              >
                <RefreshCw size={13} className={`text-white/70 ${loading ? 'animate-spin' : ''}`} />
              </button>

              {/* Avatar */}
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ring-2 ring-white/20"
                style={{ background: GOLD }}
                title={user?.first_name || 'Admin'}
              >
                <span className="text-xs font-extrabold leading-none" style={{ color: NAVY }}>
                  {(user?.first_name?.[0] || 'A').toUpperCase()}
                  {(user?.last_name?.[0] || '').toUpperCase()}
                </span>
              </div>

              {/* Logout */}
              <button
                onClick={async () => { await logout(); navigate('/login'); }}
                className="w-8 h-8 rounded-xl bg-white/10 hover:bg-red-500/40 flex items-center justify-center transition-colors"
                title="Se déconnecter"
              >
                <LogOut size={13} className="text-white/70" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── MAIN CONTENT ──────────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-4 pb-4">
        {/* Loading indicator */}
        {loading && (
          <div className="flex items-center justify-center gap-2 text-slate-400 text-xs mb-3">
            <div className="w-3 h-3 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
            Mise à jour…
          </div>
        )}

        {/* ─── STAT CARDS ─────────────────────────────────────────────────────── */}
        <div className="space-y-3 mb-5">
          {/* Row 1: Split card - Pending Loans + Pending Withdrawals */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="grid grid-cols-2 divide-x divide-slate-100">
              {/* Pending Loan Requests */}
              <button
                type="button"
                onClick={() => navigate('/admin/loan-requests')}
                className="px-4 py-4 text-left hover:bg-amber-50/50 transition-colors group"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-amber-50">
                    <Clock size={14} className="text-amber-600" />
                  </div>
                  <span className="text-slate-400 text-xs">Prêts en attente</span>
                </div>
                <p className="text-lg font-extrabold leading-none text-slate-900">
                  {stats.pendingRequests}
                </p>
                <p className="text-slate-400 text-[11px] mt-1">
                  {stats.pendingRequests === 0 ? 'Aucune demande' : 'À traiter'}
                </p>
                {stats.pendingRequests > 0 && (
                  <span className="inline-block mt-1.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">
                    Urgent
                  </span>
                )}
              </button>

              {/* Pending Withdrawals - HIGH PRIORITY */}
              <button
                type="button"
                onClick={() => navigate('/admin/ab-epargne')}
                className="px-4 py-4 text-left hover:bg-red-50/50 transition-colors group"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ background: stats.pendingWithdrawals > 0 ? '#FEE2E2' : '#F1F5F9' }}
                  >
                    <ArrowDownCircle
                      size={14}
                      style={{ color: stats.pendingWithdrawals > 0 ? '#DC2626' : '#94A3B8' }}
                    />
                  </div>
                  <span className="text-slate-400 text-xs">Retraits en attente</span>
                </div>
                <p
                  className="text-lg font-extrabold leading-none"
                  style={{ color: stats.pendingWithdrawals > 0 ? '#DC2626' : '#0f172a' }}
                >
                  {stats.pendingWithdrawals}
                </p>
                <p className="text-slate-400 text-[11px] mt-1">
                  {stats.pendingWithdrawals === 0 ? 'Aucune demande' : 'À valider'}
                </p>
                {stats.pendingWithdrawals > 0 && (
                  <span className="inline-block mt-1.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold animate-pulse">
                    Prioritaire
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Row 2: 4 individual stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Active Loans */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 px-4 py-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#EFF6FF' }}>
                  <Activity size={14} style={{ color: '#2563EB' }} />
                </div>
                <span className="text-slate-400 text-xs">Prêts actifs</span>
              </div>
              <p className="text-lg font-extrabold leading-none text-slate-900">{stats.activeLoans}</p>
              <p className="text-slate-400 text-[11px] mt-1">En cours</p>
            </div>

            {/* Interest Collected */}
            <button
              type="button"
              onClick={() => navigate('/admin/analytics')}
              className="bg-white rounded-2xl shadow-sm border border-slate-100 px-4 py-4 text-left hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#F0FDF4' }}>
                  <Wallet size={14} style={{ color: '#16A34A' }} />
                </div>
                <span className="text-slate-400 text-xs">Intérêts</span>
              </div>
              <p className="text-lg font-extrabold leading-none text-slate-900">
                {formatCurrency(stats.totalInterestCollected)}
              </p>
              <p className="text-slate-400 text-[11px] mt-1">Collectés</p>
            </button>

            {/* Active Savings Plans */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 px-4 py-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#F5F3FF' }}>
                  <PiggyBank size={14} style={{ color: '#7C3AED' }} />
                </div>
                <span className="text-slate-400 text-xs">Plans épargne</span>
              </div>
              <p className="text-lg font-extrabold leading-none text-slate-900">{stats.activeSavingsPlans}</p>
              <p className="text-slate-400 text-[11px] mt-1">Actifs</p>
            </div>

            {/* Total Users */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 px-4 py-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-slate-100">
                  <Users size={14} className="text-slate-500" />
                </div>
                <span className="text-slate-400 text-xs">Utilisateurs</span>
              </div>
              <p className="text-lg font-extrabold leading-none text-slate-900">{stats.totalUsers}</p>
              <p className="text-slate-400 text-[11px] mt-1">Inscrits</p>
            </div>
          </div>
        </div>

        {/* ─── QUICK ACTIONS ──────────────────────────────────────────────────── */}
        <div className="mb-5">
          <p className="text-slate-400 text-[11px] font-semibold uppercase tracking-wider mb-3">
            Actions rapides
          </p>
          <div className="space-y-2">
            {/* AB Prêt */}
            <button
              onClick={() => navigate('/admin/loan-requests')}
              className="w-full flex items-center justify-between px-4 py-3.5 bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md active:scale-[0.99] transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#EFF6FF' }}>
                  <CreditCard size={18} style={{ color: '#2563EB' }} />
                </div>
                <div className="text-left">
                  <p className="font-bold text-slate-900 text-sm">AB Prêt</p>
                  <p className="text-slate-400 text-xs">Gérer les demandes de prêts</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {stats.pendingRequests > 0 && (
                  <span
                    className="w-5 h-5 rounded-full text-white text-[10px] font-extrabold flex items-center justify-center shadow-sm"
                    style={{ background: NAVY }}
                  >
                    {stats.pendingRequests > 9 ? '9+' : stats.pendingRequests}
                  </span>
                )}
                <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
              </div>
            </button>

            {/* AB Épargne */}
            <button
              onClick={() => navigate('/admin/ab-epargne')}
              className="w-full flex items-center justify-between px-4 py-3.5 bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md active:scale-[0.99] transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#F0FDF4' }}>
                  <PiggyBank size={18} style={{ color: '#16A34A' }} />
                </div>
                <div className="text-left">
                  <p className="font-bold text-slate-900 text-sm">AB Épargne</p>
                  <p className="text-slate-400 text-xs">
                    Gérer les plans d'épargne
                    {stats.pendingWithdrawals > 0 && (
                      <span className="ml-1 text-red-600 font-semibold">
                        · {stats.pendingWithdrawals} retrait{stats.pendingWithdrawals > 1 ? 's' : ''} en attente
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(stats.activeSavingsPlans > 0 || stats.pendingWithdrawals > 0) && (
                  <span
                    className="w-5 h-5 rounded-full text-white text-[10px] font-extrabold flex items-center justify-center shadow-sm"
                    style={{ background: stats.pendingWithdrawals > 0 ? '#DC2626' : NAVY }}
                  >
                    {stats.pendingWithdrawals > 0
                      ? (stats.pendingWithdrawals > 9 ? '9+' : stats.pendingWithdrawals)
                      : (stats.activeSavingsPlans > 9 ? '9+' : stats.activeSavingsPlans)}
                  </span>
                )}
                <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
              </div>
            </button>

            {/* Analytics */}
            <button
              onClick={() => navigate('/admin/analytics')}
              className="w-full flex items-center justify-between px-4 py-3.5 bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md active:scale-[0.99] transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#FFF7ED' }}>
                  <BarChart3 size={18} style={{ color: '#EA580C' }} />
                </div>
                <div className="text-left">
                  <p className="font-bold text-slate-900 text-sm">Analytics</p>
                  <p className="text-slate-400 text-xs">Voir les statistiques détaillées</p>
                </div>
              </div>
              <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
            </button>

            {/* Transactions */}
            <button
              onClick={() => navigate('/admin/transactions')}
              className="w-full flex items-center justify-between px-4 py-3.5 bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md active:scale-[0.99] transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#F5F3FF' }}>
                  <Receipt size={18} style={{ color: '#7C3AED' }} />
                </div>
                <div className="text-left">
                  <p className="font-bold text-slate-900 text-sm">Transactions</p>
                  <p className="text-slate-400 text-xs">Voir toutes les transactions</p>
                </div>
              </div>
              <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
            </button>
          </div>
        </div>

        {/* ─── RECENT ACTIVITIES ──────────────────────────────────────────────── */}
        <div>
          <p className="text-slate-400 text-[11px] font-semibold uppercase tracking-wider mb-3">
            Activités récentes
          </p>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            {recentActivities.length === 0 ? (
              <div className="p-8 text-center">
                <Activity size={32} className="mx-auto text-slate-200 mb-3" />
                <p className="text-slate-400 text-sm font-medium">Aucune activité récente</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {recentActivities.map((activity) => {
                  const isWithdrawal = activity.type === 'withdrawal';
                  const isPendingLoan = activity.type === 'loan' && activity.status === 'pending';

                  const iconBg = isWithdrawal
                    ? '#FEE2E2'
                    : activity.status === 'pending'
                    ? '#FEF3C7'
                    : activity.status === 'completed'
                    ? '#F0FDF4'
                    : '#EFF6FF';

                  const iconColor = isWithdrawal
                    ? '#DC2626'
                    : activity.status === 'pending'
                    ? '#D97706'
                    : activity.status === 'completed'
                    ? '#16A34A'
                    : '#2563EB';

                  const IconComponent = isWithdrawal
                    ? ArrowDownCircle
                    : activity.status === 'pending'
                    ? Clock
                    : activity.status === 'completed'
                    ? CheckCircle
                    : Activity;

                  const badgeText = isWithdrawal
                    ? 'Retrait'
                    : activity.status === 'pending'
                    ? 'En attente'
                    : activity.status === 'completed'
                    ? 'Remboursé'
                    : 'Actif';

                  const badgeClasses = isWithdrawal
                    ? 'bg-red-100 text-red-700'
                    : activity.status === 'pending'
                    ? 'bg-amber-100 text-amber-700'
                    : activity.status === 'completed'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-blue-100 text-blue-700';

                  return (
                    <button
                      key={`${activity.type}-${activity.id}`}
                      type="button"
                      onClick={() => {
                        if (isWithdrawal) navigate('/admin/ab-epargne');
                        else navigate('/admin/loan-requests');
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${
                        isWithdrawal ? 'hover:bg-red-50/30' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: iconBg }}
                      >
                        <IconComponent size={15} style={{ color: iconColor }} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-slate-800 text-sm font-semibold truncate">
                            {isWithdrawal ? 'Demande de retrait' : 'Demande de prêt'}
                          </p>
                          <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeClasses}`}>
                            {badgeText}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-slate-900 text-xs font-bold">{formatCurrency(activity.amount)}</span>
                          <span className="text-slate-300 text-xs">·</span>
                          <span className="text-slate-400 text-xs">
                            {new Date(activity.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                      </div>

                      <ChevronRight size={14} className="flex-shrink-0 text-slate-300" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal d'alerte de fidélité pour l'admin */}
      <AdminLoyaltyAlertModal
        isOpen={showLoyaltyModal}
        onClose={() => setShowLoyaltyModal(false)}
        userName={loyaltyModalData?.userName || 'Un utilisateur'}
        userId={loyaltyModalData?.userId}
        onContactUser={handleContactUser}
      />
    </div>
  );
};

export default AdminDashboard;
